import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CodeHighlighter } from '@uhg-abyss/web/ui/CodeHighlighter';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { LagoonContext } from '@src/AppContext';

const BodyContainer = styled('div', {
  marginTop: 24,
});

export const BodyV3 = ({ body, currentApi }) => {
  const service = 'superconnector';
  const formattedCode = JSON.stringify(body, null, 2);
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('configs');
  const [bodySchema, setBodySchema] = useState({});
  const serviceDescriptionToggle = featureToggles?.getRow(
    'SERVICE_DESCRIPTION_TOGGLE'
  )?.value;
  const { schema } = currentApi;

  const asteriskText = (key) => {
    return (
      <span>
        {key}
        <span style={{ color: '#C40000' }}> *</span>
      </span>
    );
  };

  const col = [
    {
      Header: 'Field',
      accessor: 'field',
    },
    {
      Header: 'Type',
      accessor: 'type',
      maxWidth: 50,
    },
  ];
  if (serviceDescriptionToggle?.includes(service)) {
    col.push({ Header: 'Description', accessor: 'description' });
  }

  const columns = React.useMemo(() => {
    return col;
  }, [service]);
  const formattedSchema = (data, requiredFields = []) => {
    if (!data) return [];
    const schemaData = Object.keys(data).map((key) => {
      const value = data[key];
      let { type } = value;
      if (data[key].type === 'object') {
        type = 'object';
        const subRows = formattedSchema(value.properties, value?.required);
        return {
          field: requiredFields.includes(key) ? asteriskText(key) : key,
          type,
          subRows,
          description: value?.description?.substring(0, 350),
        };
      }
      if (data[key].type === 'array') {
        type = 'array';
        let subRows = [];
        if (value?.items?.properties) {
          subRows = formattedSchema(
            value.items?.properties,
            value.items?.required
          );
        } else if (value?.items?.type === 'object') {
          subRows = formattedSchema(
            value.items?.properties || {},
            value.items?.required
          );
        }

        return {
          field: requiredFields.includes(key) ? asteriskText(key) : key,
          type: value?.items?.type ? `array[${value.items.type}]` : 'array',
          subRows,
          description: (
            value?.description ||
            value?.items?.description ||
            ''
          )?.substring(0, 350),
        };
      }
      return {
        field: requiredFields.includes(key) ? asteriskText(key) : key,
        type,
        description: value?.description?.substring(0, 350),
      };
    });
    return schemaData;
  };
  const data = useMemo(() => {
    return [schema && Object.keys(schema).length > 0];
  }, [currentApi]);

  const dataTableProps = useDataTable({
    initialData: data,
    initialColumns: columns,
    showExpansion: true,
  });

  useEffect(() => {
    const requestBody = currentApi.requestBody;
    if (!requestBody || !requestBody.content) {
      setBodySchema({});
      return;
    }
    const contentTypes = Object.keys(requestBody.content);
    if (contentTypes.length === 0) {
      setBodySchema({});
      return;
    }
    const content = requestBody.content[contentTypes[0]];
    if (!content) {
      setBodySchema({});
      return;
    }
    if (!content.schema) {
      setBodySchema({});
      return;
    }
    setBodySchema(content.schema);
  }, [currentApi]);

  useEffect(() => {
    dataTableProps.setColumns(columns, true);
    dataTableProps.setData(
      formattedSchema(bodySchema.properties, bodySchema?.required || []),
      true
    );
  }, [bodySchema]);

  return (
    <BodyContainer>
      <Tabs title="Arrow Variant" variant="arrow">
        <Tabs.Tab label="Schema">
          <DataTable tableState={dataTableProps} />
        </Tabs.Tab>
        <Tabs.Tab label="Sample">
          <CodeHighlighter
            language="js"
            code={formattedCode}
            maxHeight="450px"
          />
        </Tabs.Tab>
      </Tabs>
    </BodyContainer>
  );
};

BodyV3.propTypes = {
  body: PropTypes.shape({}),
  currentApi: PropTypes.shape({}).isRequired,
};

BodyV3.defaultProps = {
  body: null,
};
